import { css } from '@emotion/core'
import { ContentAdd, ContentDelete, ContentHistory } from '@fs/zion-icon'
import {
  Button,
  Divider,
  Grid,
  IconButton,
  LabelTooltip,
  ResponsiveTableRow,
  Row,
  Select,
  Separator,
  TextField,
  H5,
  H3,
  H4,
} from '@fs/zion-ui'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveTable } from '../DmcResponsiveTable'
import {
  getBooleanDisplayValue,
  getLastPathElement,
  getMrnFromIdentifier,
  getPidFromIdentifier,
  getSourceSystemIdFromIdentifier,
  setIdInIdentifiers,
} from '../../../util/StringUtil'
import { MrnLink, PersonLink } from '../../app/Links'
import DmcDataView from '../DmcDataView'
import { DmcDatePicker } from '../DmcDatePicker/DmcDatePicker'
import Editable from '../Editable'
import { DmcNewConclusionFact } from '../conclusionFact/DmcNewConclusionFact'
import CtrFlag from '../../ctr/CtrFlag'
import DmcVerifiedPersonIdTextField from '../DmcVerifiedPersonIdTextField'
import DmcVerifiedMrnTextField from '../DmcVerifiedMrnTextField'
import { getFrontendBaseUrl } from '../../../util/UrlUtil'
import DmcCopyToClipboardTooltip from '../../../util/DmcCopyToClipboardTooltip'
import { getDuplicatePersonIds, normalizePid } from '../../../util/TemplePlatformUtil'
import { getPersonSummary } from '../../../services/personService'

const marginTop28 = css`
  margin-top: 28px;
`

export default function CtrOwsSummaryPerson({
  personDataId,
  personIndex,
  editableTemplePlatform,
  setEditableTemplePlatform,
  editableCtrExtendedDetail,
  setEditableCtrExtendedDetail,
  isEditing,
  setVerifiedPersons,
  setVerifiedMembers,
}) {
  const { t } = useTranslation()
  const [nameFormIndexToFocus, setNameFormIndexToFocus] = useState()
  const [namePartIndexToFocus, setNamePartIndexToFocus] = useState()
  const nameTypeRef = useRef()
  const pid = editableTemplePlatform.persons[personIndex].id
  const personRoleLabel = getPersonRoleLabel(editableCtrExtendedDetail, pid)
  const hasQualifyingRelationships =
    editableCtrExtendedDetail.qualifyingRelationships &&
    editableCtrExtendedDetail.qualifyingRelationships.length > 0 &&
    editableCtrExtendedDetail.qualifyingRelationships.some((qualifyingRelationship) =>
      qualifyingRelationship.resource.includes(pid)
    )

  useEffect(() => {
    if (nameTypeRef.current) {
      nameTypeRef.current.focus()
    }
  }, [namePartIndexToFocus, nameFormIndexToFocus])

  function getPersonRoleLabel(ctrDetail, personId) {
    if (ctrDetail.principal !== null && personId === ctrDetail.principal.resourceId) {
      return t('principal', 'Principal')
    }
    if (ctrDetail.wife && ctrDetail.wife !== null && personId === ctrDetail.wife.resourceId) {
      return t('wife', 'Wife')
    }
    if (ctrDetail.father && ctrDetail.father !== null && personId === ctrDetail.father.resourceId) {
      return t('father', 'Father')
    }
    if (ctrDetail.mother && ctrDetail.mother !== null && personId === ctrDetail.mother.resourceId) {
      return t('mother', 'Mother')
    }
    return t('undefined', 'Undefined')
  }

  return (
    <>
      <Separator size="lg" />
      <H3 size="H5">{personRoleLabel}</H3>
      <PersonDetails
        personDataId={personDataId}
        personIndex={personIndex}
        editableTemplePlatform={editableTemplePlatform}
        setEditableTemplePlatform={setEditableTemplePlatform}
        isEditing={isEditing}
        nameFormIndexToFocus={nameFormIndexToFocus}
        setNameFormIndexToFocus={setNameFormIndexToFocus}
        namePartIndexToFocus={namePartIndexToFocus}
        setNamePartIndexToFocus={setNamePartIndexToFocus}
        nameTypeRef={nameTypeRef}
        setVerifiedPersons={setVerifiedPersons}
        setVerifiedMembers={setVerifiedMembers}
      />
      <Separator />
      <PersonFacts
        personDataId={personDataId}
        personIndex={personIndex}
        editableTemplePlatform={editableTemplePlatform}
        setEditableTemplePlatform={setEditableTemplePlatform}
        isEditing={isEditing}
      />
      {hasQualifyingRelationships && (
        <>
          <Separator />
          <PersonQualifyingRelationships
            personIndex={personIndex}
            editableTemplePlatform={editableTemplePlatform}
            setEditableTemplePlatform={setEditableTemplePlatform}
            editableCtrExtendedDetail={editableCtrExtendedDetail}
            setEditableCtrExtendedDetail={setEditableCtrExtendedDetail}
            isEditing={isEditing}
            setVerifiedPersons={setVerifiedPersons}
            setVerifiedMembers={setVerifiedMembers}
          />
        </>
      )}
      <Separator />
      <Divider />
    </>
  )
}

function PersonDetails({
  personDataId,
  personIndex,
  editableTemplePlatform,
  setEditableTemplePlatform,
  isEditing,
  nameFormIndexToFocus,
  setNameFormIndexToFocus,
  namePartIndexToFocus,
  setNamePartIndexToFocus,
  nameTypeRef,
  setVerifiedPersons,
  setVerifiedMembers,
}) {
  const { t } = useTranslation()
  const person = editableTemplePlatform.persons[personIndex]
  const personIds = person.identifiers
  const pid = getPidFromIdentifier(personIds)
  const mrn = getMrnFromIdentifier(personIds)
  const living = person.living
  const gender = person.gender?.type
  const names = person.names
  const personDetailsColumns = 3
  const duplicateIds = getDuplicatePersonIds(editableTemplePlatform.persons)
  const duplicatePidMessage = duplicateIds.pids.has(normalizePid(pid))
    ? t('ctr.duplicatePid', 'PID is already used by another person on this record')
    : null
  const duplicateMrnMessage = duplicateIds.mrns.has(mrn)
    ? t('ctr.duplicateMrn', 'MRN is already used by another person on this record')
    : null
  const [isLegacyPerson, setIsLegacyPerson] = useState(false)

  useEffect(() => {
    let isMounted = true
    // Reset immediately on every pid change so a stale `true` from a previous
    // pid can't stay visible while the new pid's lookup is still in flight
    // (or never happens because pid is falsy).
    setIsLegacyPerson(false)
    async function fetchLegacyStatus() {
      try {
        const personSummary = await getPersonSummary(pid)
        if (isMounted) {
          setIsLegacyPerson(Boolean(personSummary?.synthetic))
        }
      } catch {
        // Unable to determine legacy status; fail closed and keep the indicator hidden.
        if (isMounted) {
          setIsLegacyPerson(false)
        }
      }
    }
    if (pid) {
      fetchLegacyStatus()
    }
    return () => {
      isMounted = false
    }
  }, [pid])

  if (!editableTemplePlatform.persons[personIndex].names) {
    const nameParts = [
      {
        nameForms: [
          {
            parts: [],
          },
        ],
      },
    ]
    editableTemplePlatform.persons[personIndex].names = JSON.parse(JSON.stringify(nameParts))
  }

  const editField = (field, value) => {
    const newTemplePlatform = {
      ...editableTemplePlatform,
    }
    switch (field) {
      case 'personId': {
        if (!newTemplePlatform.persons[personIndex].identifiers) {
          newTemplePlatform.persons[personIndex].identifiers = {}
        }
        setIdInIdentifiers(
          newTemplePlatform.persons[personIndex].identifiers,
          `p.${value}`,
          'http://api.familysearch.org/temple/USYS_ID'
        )
        break
      }
      case 'mrn': {
        if (!newTemplePlatform.persons[personIndex].identifiers) {
          newTemplePlatform.persons[personIndex].identifiers = {}
        }
        setIdInIdentifiers(
          newTemplePlatform.persons[personIndex].identifiers,
          value,
          'http://api.familysearch.org/temple/MEMBERSHIP_RECORD_NUMBER'
        )
        break
      }
      case 'living': {
        newTemplePlatform.persons[personIndex].living = value === 'Yes'
        break
      }
      case 'gender': {
        newTemplePlatform.persons[personIndex].gender.type = value
        break
      }
      default: {
        break
      }
    }
    setEditableTemplePlatform(newTemplePlatform)
  }

  const editNameField = (field, nameFormIndex, namePartIndex, value) => {
    const newTemplePlatform = { ...editableTemplePlatform }
    switch (field) {
      case 'lang': {
        newTemplePlatform.persons[personIndex].names[0].nameForms[nameFormIndex].lang = value
        break
      }
      case 'delete part': {
        newTemplePlatform.persons[personIndex].names[0].nameForms[nameFormIndex].parts.splice(namePartIndex, 1)
        const namePartsLength = newTemplePlatform.persons[personIndex].names[0].nameForms[nameFormIndex].parts.length
        setNamePartIndexToFocus(namePartsLength - 1)
        setNameFormIndexToFocus(nameFormIndex)
        if (newTemplePlatform.persons[personIndex].names[0].nameForms[nameFormIndex].parts.length === 0) {
          newTemplePlatform.persons[personIndex].names[0].nameForms.splice(nameFormIndex, 1)
        }
        if (newTemplePlatform.persons[personIndex].names[0].nameForms.length === 0) {
          newTemplePlatform.persons[personIndex].names[0].nameForms.push({ parts: [] })
        }
        break
      }
      case 'edit type': {
        newTemplePlatform.persons[personIndex].names[0].nameForms[nameFormIndex].parts[namePartIndex].type = value
        break
      }
      case 'edit part': {
        newTemplePlatform.persons[personIndex].names[0].nameForms[nameFormIndex].parts[namePartIndex].value = value
        break
      }
      case 'add part': {
        if (nameFormIndex === undefined) {
          nameFormIndex = 0
        }
        if (!newTemplePlatform.persons[personIndex].names[0].nameForms[nameFormIndex]) {
          newTemplePlatform.persons[personIndex].names[0].nameForms.push({ parts: [] })
        }
        newTemplePlatform.persons[personIndex].names[0].nameForms[nameFormIndex].parts.push({ type: null, value: null })
        setNamePartIndexToFocus(
          newTemplePlatform.persons[personIndex].names[0].nameForms[nameFormIndex].parts.length - 1
        )
        setNameFormIndexToFocus(nameFormIndex)
        break
      }
      default: {
        break
      }
    }
    setEditableTemplePlatform(newTemplePlatform)
  }

  const setIsVerifiedPerson = useCallback(
    (isVerifiedPerson) =>
      setVerifiedPersons((prevState) => {
        const prevStateCopy = { ...prevState }
        prevStateCopy[`Person ${personIndex}`] = isVerifiedPerson
        return prevStateCopy
      }),
    [personIndex, setVerifiedPersons]
  )

  const setIsVerifiedMrn = useCallback(
    (isVerifiedMember) => {
      setVerifiedMembers((prevState) => {
        const prevStateCopy = { ...prevState }
        prevStateCopy[`Member ${personIndex}`] = isVerifiedMember
        return prevStateCopy
      })
    },
    [personIndex, setVerifiedMembers]
  )

  const getSourceSystemElement = () => {
    const identifier = getSourceSystemIdFromIdentifier(personIds)
    if (identifier?.includes('ODM') && identifier?.split(':')) {
      const odmSearch = {
        ODM: 'odmRecords',
        ODMIJ: 'japan',
        ODMIK: 'korea',
        ODMIC: 'china',
        ODMIF: 'cyrillic',
      }
      return (
        <DmcCopyToClipboardTooltip
          value={identifier.split(':')[1]}
          body={identifier}
          path={`${getFrontendBaseUrl()}/search/odv?search=${
            odmSearch[identifier.split(':')[0]] || 'odmRecords'
          }&recordNbr=${identifier.split(':')[1]}`}
          newTab
        />
      )
    }
    return <DmcCopyToClipboardTooltip value={identifier} />
  }

  const personData = [
    {
      label: t('pid', 'PID'),
      detail: (
        <>
          <Editable text={pid} type="personLink" isEdit={isEditing}>
            <DmcVerifiedPersonIdTextField
              value={pid}
              onChange={(e) => {
                editField('personId', e.target.value)
              }}
              setIsVerifiedPerson={setIsVerifiedPerson}
              errorMessage={duplicatePidMessage}
            />
          </Editable>
          {isLegacyPerson && (
            <LabelTooltip
              label={t('ctr.legacyPidTooltip', 'Legacy — not restorable, merged in a prior system')}
              target={
                <span aria-label={t('ctr.legacyPid', 'Legacy PID')} data-testid="legacyPidIndicator">
                  <ContentHistory size="xs" />
                </span>
              }
            />
          )}
        </>
      ),
      key: `${personDataId}-pid`,
    },
    {
      label: t('ctr.mrn', 'MRN'),
      detail: (
        <Editable text={mrn} type="mrnLink" isEdit={isEditing}>
          <DmcVerifiedMrnTextField
            value={mrn}
            onChange={(e) => {
              editField('mrn', e.target.value.trim())
            }}
            setIsVerifiedMember={setIsVerifiedMrn}
            errorMessage={duplicateMrnMessage}
          />
        </Editable>
      ),
      key: `${personDataId}-mrn`,
    },
    {
      label: t('ctr.living', 'Living'),
      detail: (
        <Editable text={living !== undefined && living !== null && getBooleanDisplayValue(living)} isEdit={isEditing}>
          <CtrFlag
            value={living}
            onChange={(value) => {
              editField('living', value)
            }}
            name={`${personDataId}-ctr-flag-living`}
          />
        </Editable>
      ),
      key: `${personDataId}-living`,
    },
    {
      label: t('ctr.sourceSystemId', 'Source System ID'),
      detail: personIds && getSourceSystemElement(),
      key: `${personDataId}-source-system-id`,
    },
    {
      label: t('ctr.gender', 'Gender'),
      detail: (
        <Editable text={gender && getLastPathElement(gender)} isEdit={isEditing}>
          <div style={{ display: 'inline-block' }}>
            <Select
              id="selectGender"
              value={gender}
              onChange={(e) => {
                editField('gender', e.target.value)
              }}
              options={[
                { value: 'http://gedcomx.org/Male', label: t('gender.male', 'Male') },
                { value: 'http://gedcomx.org/Female', label: t('gender.female', 'Female') },
              ]}
            />
          </div>
        </Editable>
      ),
      key: `${personDataId}-gender`,
    },
  ]

  if (names && names.length > 0) {
    const nameData = names[0].nameForms.map((nameForm, nameFormIndex) => {
      const nameParts = nameForm.parts
      return {
        label: t('ctr.nameWithNumber', 'Name ({lang})', { lang: nameForm.lang }),
        detail: (
          <Editable text={nameForm.fullText} isEdit={isEditing}>
            <br />
            <b>{t('language', 'Language')}</b>
            <TextField
              placeholder={nameForm.lang}
              value={nameForm.lang}
              onChange={(e) => {
                editNameField('lang', nameFormIndex, null, e.target.value)
              }}
            />
            <b>{t('name', 'Name')}</b>
            {nameParts &&
              nameParts.length > 0 &&
              nameParts.map((namePart, namePartIndex) => {
                return (
                  <Row
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${personDataId}-${nameForm.id}-${namePartIndex}-name-part`}
                    gutters="nano"
                    guttersY="none"
                    wrap
                  >
                    <IconButton
                      data-testid="deleteNamePartButton"
                      Icon={ContentDelete}
                      onClick={() => editNameField('delete part', nameFormIndex, namePartIndex, null)}
                      color="red"
                      emphasis="high"
                    />
                    <Select
                      options={[
                        { value: 'http://gedcomx.org/Given', label: t('name.parts.given', 'Given') },
                        { value: 'http://gedcomx.org/Surname', label: t('name.parts.surname', 'Surname') },
                        { value: 'http://gedcomx.org/Prefix', label: t('name.parts.prefix', 'Prefix') },
                        { value: 'http://gedcomx.org/Suffix', label: t('name.parts.suffix', 'Suffix') },
                        { value: 'http://gedcomx.org/Other', label: t('name.parts.other', 'Other') },
                      ]}
                      value={namePart.type}
                      onChange={(e) => editNameField('edit type', nameFormIndex, namePartIndex, e.target.value)}
                      ref={
                        namePartIndexToFocus === namePartIndex && nameFormIndexToFocus === nameFormIndex
                          ? nameTypeRef
                          : null
                      }
                    />
                    <TextField
                      placeholder={namePart.value}
                      value={namePart.value}
                      onChange={(e) => editNameField('edit part', nameFormIndex, namePartIndex, e.target.value)}
                      growX
                    />
                  </Row>
                )
              })}
            {nameParts.length < 5 && (
              <IconButton
                data-testid="addNamePartButton"
                Icon={ContentAdd}
                color="green"
                onClick={() => editNameField('add part', nameFormIndex, null, null)}
                emphasis="high"
              />
            )}
          </Editable>
        ),
        key: `${personDataId}-${nameForm.id}-name-form`,
      }
    })
    if (nameData) {
      nameData.forEach((name, index) => {
        personData.splice(personDetailsColumns + index, 0, name)
      })
    }
  }
  personData.push({
    label: null,
    detail: (
      <Editable isEdit={isEditing}>
        <Separator size="xs" />
        <Button
          data-testid="addNewNameButton"
          onClick={() => editNameField('add part', person.names?.[0]?.nameForms?.length, null, null)}
          emphasis="medium"
        >
          {t('ctr.addNewName', 'Add New Name')}
        </Button>
      </Editable>
    ),
    key: `${personDataId}-add-new-name-button`,
  })

  return <DmcDataView id="Person Details" data={personData} columns={personDetailsColumns} />
}

function PersonFacts({ personDataId, personIndex, editableTemplePlatform, setEditableTemplePlatform, isEditing }) {
  const facts = editableTemplePlatform.persons[personIndex].facts
  const { t } = useTranslation()
  const tableLabels = getTableLabels()

  function getTableLabels() {
    const hasLegacyEventId = facts?.some((fact) => {
      if (fact.qualifiers && fact.qualifiers.length > 0) {
        return fact.qualifiers.some(
          (qualifier) => qualifier.name === 'http://api.familysearch.org/temple/LEGACY_EVENT_ID'
        )
      }
      return false
    })
    if (facts && facts.length > 0 && hasLegacyEventId) {
      return {
        labels: [
          t('type', 'Type'),
          `${t('value', 'Value')}/${t('date', 'Date')}`,
          t('ctr.Place', 'Place'),
          t('ctr.legacyEventId', 'Legacy Event Id'),
        ],
      }
    }
    return {
      labels: [t('type', 'Type'), `${t('value', 'Value')}/${t('date', 'Date')}`, t('ctr.place', 'Place')],
    }
  }

  const factOnChange = (value, factIndex, valueType) => {
    const newTemplePlatform = {
      ...editableTemplePlatform,
    }
    switch (valueType) {
      case 'value':
        newTemplePlatform.persons[personIndex].facts[factIndex].value = value
        break
      case 'date':
        newTemplePlatform.persons[personIndex].facts[factIndex].date.original = value ? value.formattedDate : ''
        break
      case 'place':
        newTemplePlatform.persons[personIndex].facts[factIndex].place.original = value
        break
      default:
        break
    }
    setEditableTemplePlatform(newTemplePlatform)
  }

  const addFactToTable = (newConclusionFact) => {
    const newTemplePlatform = {
      ...editableTemplePlatform,
    }
    if (!newTemplePlatform.persons[personIndex].facts) {
      newTemplePlatform.persons[personIndex].facts = []
    }
    newTemplePlatform.persons[personIndex].facts.push(newConclusionFact.value)
    setEditableTemplePlatform(newTemplePlatform)
  }

  const deleteFactFromTable = (factToDelete) => {
    const newTemplePlatform = {
      ...editableTemplePlatform,
    }
    newTemplePlatform.persons[personIndex].facts = newTemplePlatform.persons[personIndex].facts.filter(
      (fact) => fact !== factToDelete
    )
    setEditableTemplePlatform(newTemplePlatform)
  }

  return (
    <div id="Facts">
      {facts && facts.length > 0 ? (
        <Grid>
          <H4 columns={1} supplementary size="H5">
            {t('facts', 'Facts')}
          </H4>
          <DmcResponsiveTable
            columns={11}
            atSizeColumns={{
              default: tableLabels,
            }}
            totalColumnCount={tableLabels.labels.length}
            verticalAlign="middle"
          >
            {facts.map((fact, factIndex) => {
              const legacyEventQualifier =
                tableLabels.labels.length === 4 &&
                fact.qualifiers &&
                fact.qualifiers.find(
                  (qualifier) => qualifier.name === 'http://api.familysearch.org/temple/LEGACY_EVENT_ID'
                )
              let factType = fact.type && getLastPathElement(fact.type)
              if (factType === 'UNITNUMBER') {
                factType = 'Unit Number'
              }
              if (factType === 'MarriageLicense') {
                factType = 'Marriage License'
              }
              return (
                <ResponsiveTableRow
                  key={`${personDataId}-${fact.id || fact}-fact`}
                  column1={
                    <Row>
                      {isEditing && (
                        <IconButton
                          Icon={ContentDelete}
                          onClick={() => deleteFactFromTable(fact)}
                          color="red"
                          emphasis="high"
                        />
                      )}
                      {factType}
                    </Row>
                  }
                  column2={
                    <>
                      {fact.value ? (
                        <Editable text={fact.value} type={factType} isEdit={isEditing}>
                          <TextField
                            placeholder={fact.value?.toString()}
                            value={fact.value}
                            onChange={(e) =>
                              factOnChange(
                                e.target.value,
                                factIndex,
                                fact.value !== null && fact.value !== undefined && 'value'
                              )
                            }
                          />
                        </Editable>
                      ) : (
                        <Editable text={fact.date.original} type={factType} isEdit={isEditing}>
                          <DmcDatePicker
                            label=" "
                            selectedItem={{ ...fact.date, primaryText: fact.date.original }}
                            setSelectedItem={(e) => factOnChange(e, factIndex, 'date')}
                            dayMonthYearDateFormat="DD MMMM YYYY"
                            monthYearDateFormat="MMMM yyyy"
                            yearDateFormat="yyyy"
                          />
                        </Editable>
                      )}
                    </>
                  }
                  column3={
                    fact.place && (
                      <Editable text={fact.place.original} isEdit={isEditing}>
                        <div css={marginTop28}>
                          <TextField
                            placeholder={fact.place.original}
                            value={facts[factIndex].place.original}
                            onChange={(e) => factOnChange(e.target.value, factIndex, 'place')}
                          />
                        </div>
                      </Editable>
                    )
                  }
                  column4={legacyEventQualifier ? legacyEventQualifier.value : null}
                />
              )
            })}
          </DmcResponsiveTable>
        </Grid>
      ) : (
        <>{isEditing && <H5 supplementary>{t('ctr.summary.headings.addAFact', 'Add a Fact')}</H5>}</>
      )}
      {isEditing && (
        <DmcNewConclusionFact onCreateCallback={(newConclusionFact) => addFactToTable(newConclusionFact)} />
      )}
    </div>
  )
}

function PersonQualifyingRelationships({
  personIndex,
  editableTemplePlatform,
  setEditableTemplePlatform,
  editableCtrExtendedDetail,
  setEditableCtrExtendedDetail,
  isEditing,
  setVerifiedPersons,
  setVerifiedMembers,
}) {
  const { t } = useTranslation()
  const persons = editableTemplePlatform.persons
  const relationships = editableTemplePlatform.relationships
  const qualifyingRelationships = editableCtrExtendedDetail.qualifyingRelationships
  const qualifyingRelationshipData = getQualifyingRelationships()

  function getQualifyingRelationships() {
    return qualifyingRelationships
      .filter((qualifyingRelationship) => qualifyingRelationship.resource.includes(persons[personIndex].id))
      .map((qualifyingRelationship) => {
        const relationshipDataItem = {
          id: qualifyingRelationship.resourceId,
          name: null,
          pid: null,
          mrn: null,
          relationshipType: null,
        }
        const relationship = relationships.find((r) => qualifyingRelationship.resource.substring(1) === r.id)
        const relationshipType = getLastPathElement(relationship.type)
        const familyMember = persons.find((person) => relationship.person1.resourceId === person.id)
        relationshipDataItem.name = familyMember?.names?.[0]?.nameForms?.[0]?.fullText
        relationshipDataItem.pid = getPidFromIdentifier(familyMember.identifiers)
        relationshipDataItem.mrn = getMrnFromIdentifier(familyMember.identifiers)
        relationshipDataItem.relationshipType = relationshipType
        return relationshipDataItem
      })
  }

  const editField = (type, value, qrId) => {
    const qrIdWithPrefix = `qr.${qrId}`
    const newTemplePlatform = {
      ...editableTemplePlatform,
    }
    const newCtrExtendedDetail = {
      ...editableCtrExtendedDetail,
    }
    switch (type) {
      case 'delete qr': {
        newTemplePlatform.relationships = newTemplePlatform.relationships.filter(
          (relationship) => !relationship.id.includes(qrId)
        )
        newCtrExtendedDetail.qualifyingRelationships = newCtrExtendedDetail.qualifyingRelationships.filter(
          (qualifyingRelationship) => qualifyingRelationship.resourceId !== qrId
        )
        newTemplePlatform.persons = newTemplePlatform.persons.filter((p) => !p.id.includes(qrIdWithPrefix))
        break
      }
      case 'name': {
        const person = newTemplePlatform.persons.find((p) => p.id === qrIdWithPrefix)
        if (!person.names) {
          person.names = [
            {
              nameForms: [{}],
            },
          ]
        }
        person.names[0].nameForms[0].fullText = value
        break
      }
      case 'personId': {
        const person = newTemplePlatform.persons.find((p) => p.id === qrIdWithPrefix)
        if (!person.identifiers) {
          person.identifiers = {}
        }
        setIdInIdentifiers(person.identifiers, `p.${value}`, 'http://api.familysearch.org/temple/USYS_ID')
        break
      }
      case 'mrn': {
        const person = newTemplePlatform.persons.find((p) => p.id === qrIdWithPrefix)
        if (!person.identifiers) {
          person.identifiers = {}
        }
        setIdInIdentifiers(person.identifiers, value, 'http://api.familysearch.org/temple/MEMBERSHIP_RECORD_NUMBER')
        break
      }
      default: {
        break
      }
    }
    setEditableTemplePlatform(newTemplePlatform)
    setEditableCtrExtendedDetail(newCtrExtendedDetail)
  }

  return (
    <>
      {qualifyingRelationshipData && qualifyingRelationshipData[0]?.relationshipType && (
        <>
          <H5 supplementary>{t('ctr.summary.headings.qualifyingRelationships', 'Qualifying Relationships')}</H5>
          <Separator size="nano" />
          <Grid>
            <span columns={1} />
            <DmcResponsiveTable
              columns={11}
              atSizeColumns={{
                default: {
                  labels: [
                    `${t('name', 'Name')}, ${t('pid', 'PID')}, ${t('mrn', 'MRN')} & ${t(
                      'relationship.label',
                      'Relationship'
                    )}`,
                  ],
                },
                md: {
                  labels: [
                    t('name', 'Name'),
                    `${t('pid', 'PID')} & ${t('mrn', 'MRN')}`,
                    t('relationship.label', 'Relationship'),
                  ],
                  combinationRules: [['column1'], ['column2', 'column3'], ['column4']],
                },
                xl: {
                  labels: [
                    t('name', 'Name'),
                    t('pid', 'PID'),
                    t('mrn', 'MRN'),
                    t('relationship.label', 'Relationship'),
                  ],
                },
              }}
              totalColumnCount={4}
              separator={<br />}
            >
              {qualifyingRelationshipData.map((qualifyingRelationship, index) => {
                const name = qualifyingRelationship.name
                const personId = qualifyingRelationship.pid
                const mrn = qualifyingRelationship.mrn
                const relationshipType = qualifyingRelationship.relationshipType
                return (
                  <ResponsiveTableRow
                    key={qualifyingRelationship.id}
                    column1={
                      <Editable text={qualifyingRelationship.name && qualifyingRelationship.name} isEdit={isEditing}>
                        <Row>
                          <IconButton
                            data-testid="deleteQualifyingRelationshipButton"
                            Icon={ContentDelete}
                            onClick={() => editField('delete qr', null, qualifyingRelationship.id)}
                            color="red"
                            emphasis="high"
                          />
                          <TextField
                            placeholder={name}
                            value={name}
                            onChange={(e) => editField('name', e.target.value, qualifyingRelationship.id)}
                            growX
                          />
                        </Row>
                      </Editable>
                    }
                    column2={
                      <Editable
                        text={qualifyingRelationship.pid && <PersonLink personId={qualifyingRelationship.pid} />}
                        isEdit={isEditing}
                      >
                        <DmcVerifiedPersonIdTextField
                          value={personId}
                          onChange={(e) => {
                            editField('personId', e.target.value, qualifyingRelationship.id)
                          }}
                          setIsVerifiedPerson={(isVerifiedPerson) =>
                            setVerifiedPersons((prevState) => {
                              const prevStateCopy = { ...prevState }
                              prevStateCopy[`Person ${personIndex} - QR ${index}`] = isVerifiedPerson
                              return prevStateCopy
                            })
                          }
                        />
                      </Editable>
                    }
                    column3={
                      <Editable text={mrn && <MrnLink mrn={mrn} />} isEdit={isEditing}>
                        <DmcVerifiedMrnTextField
                          value={qualifyingRelationship.mrn}
                          onChange={(e) => editField('mrn', e.target.value, qualifyingRelationship.id)}
                          setIsVerifiedMember={(isVerifiedMember) =>
                            setVerifiedMembers((prevState) => {
                              const prevStateCopy = { ...prevState }
                              prevStateCopy[`Member ${personIndex} - QR ${index}`] = isVerifiedMember
                              return prevStateCopy
                            })
                          }
                        />
                      </Editable>
                    }
                    column4={relationshipType}
                  />
                )
              })}
            </DmcResponsiveTable>
          </Grid>
        </>
      )}
    </>
  )
}
